import React, { useState, useEffect, useCallback } from 'react';
import {
    X, ChevronRight, // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Plus, Database, Search, FileJson, Terminal
} from 'lucide-react';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import SourceTypeSelectorModal from './SourceTypeSelectorModal';
import { apiService } from '../../../../../components/api/apiService';
import SchemaDiscoveryModal from './SchemaDiscoveryModal';
import QueryBuilderModal from './QueryBuilderModal';

SyntaxHighlighter.registerLanguage('json', json);

const STRUCTURED_DB_KEYS = ['postgresql', 'pgsql', 'postgres', 'sqlserver', 'mssql', 'mysql', 'oracle', 'snowflake', 'bigquery', 'redshift', 'sqlite'];
function isStructuredDatabase(connectorKey: string | undefined): boolean {
    if (!connectorKey) return false;
    const key = connectorKey.toLowerCase();
    return STRUCTURED_DB_KEYS.some(k => key.includes(k) || key === k);
}

interface Props {
    onClose: () => void;
    onSubmit: (data: any) => void;
}

const IngestionEntityModal: React.FC<Props> = ({ onClose, onSubmit }) => {
    const [showTypeSelector, setShowTypeSelector] = useState(false);
    const [showSchemaDiscovery, setShowSchemaDiscovery] = useState(false);
    const [showQueryBuilder, setShowQueryBuilder] = useState(false);
    const [schemaData, setSchemaData] = useState<any[]>([]);
    const [loadingSchema, setLoadingSchema] = useState(false);
    const [schemaError, setSchemaError] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        sourceName: '',
        description: '',
        sourceType: '',
        connectionDetails: '',
        tags: [] as string[]
    });

    const [metadataPreview, setMetadataPreview] = useState<string | null>(null);


    const [connections, setConnections] = useState<any[]>([]);
    const [loadingConnections, setLoadingConnections] = useState(false);



    useEffect(() => {
        const fetchConnections = async () => {
            setLoadingConnections(true);
            try {
                const data = await apiService.get('/api/DataStores', {}, 'backend');
                setConnections(data.datastores || []);
            } catch (err) {
                console.error('Error fetching connections:', err);
            } finally {
                setLoadingConnections(false);
            }
        };

        fetchConnections();
    }, []);
    const handleRemoveTag = (tag: string) => {
        setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
    };

    const getSelectedConnection = useCallback(() => {
        return connections.find(c => c.name === formData.sourceType);
    }, [connections, formData.sourceType]);

    const handleSchemaDiscovery = async () => {
        if (!formData.sourceType) return;

        const selectedConn = getSelectedConnection();
        if (!selectedConn) return;

        setLoadingSchema(true);
        try {
            const data = await apiService.get(`/api/DataStores/${selectedConn.id}/discover`, {}, 'backend');
            setSchemaData(data.nodes || []);
            setShowSchemaDiscovery(true);
        } catch (err) {
            console.error('Schema discovery failed:', err);
        } finally {
            setLoadingSchema(false);
        }
    };

    const handleExpandNode = useCallback(async (nodePath: string) => {
        const selectedConn = getSelectedConnection();
        if (!selectedConn) return [];
        const data = await apiService.get(
            `/api/DataStores/${selectedConn.id}/expand?path=${encodeURIComponent(nodePath)}`,
            {},
            'backend'
        );
        return data.nodes || [];
    }, [getSelectedConnection]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
            <div className="relative w-full max-w-4xl bg-neutral-900/95 border border-neutral-700/80 rounded-xl shadow-2xl shadow-black/40 overflow-hidden ring-1 ring-white/5 flex flex-col max-h-[90vh]">

                {/* Header - compact */}
                <div className="flex justify-between items-center px-4 py-3 border-b border-neutral-800/80 bg-gradient-to-r from-neutral-900 to-neutral-900/80 shrink-0">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                            <Database className="w-4 h-4 text-indigo-400" />
                        </div>
                        <div>
                            <h2 className="text-white font-semibold text-sm">Ingestion Entity</h2>
                            <p className="text-[11px] text-gray-500">Configure data source</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-neutral-800 transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Content - two columns: form left, metadata template right */}
                <div className="flex flex-1 min-h-0 p-4 gap-4">
                    {/* Left: form */}
                    <div className="flex flex-col gap-4 w-[380px] shrink-0 overflow-y-auto custom-scrollbar">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Source Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Sales DB"
                                    className="w-full bg-neutral-800/80 border border-neutral-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:ring-1 focus:ring-indigo-500/60 focus:border-indigo-500/40 outline-none transition-all"
                                    value={formData.sourceName}
                                    onChange={e => setFormData({ ...formData, sourceName: e.target.value })}
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Description</label>
                                <input
                                    type="text"
                                    placeholder="Brief description"
                                    className="w-full bg-neutral-800/80 border border-neutral-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:ring-1 focus:ring-indigo-500/60 focus:border-indigo-500/40 outline-none transition-all"
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Source Type *</label>
                            <div className="relative">
                                <select
                                    className="w-full bg-neutral-800/80 border border-neutral-700/50 rounded-lg px-3 py-2 pr-9 text-sm text-white focus:ring-1 focus:ring-indigo-500/60 focus:border-indigo-500/40 outline-none appearance-none cursor-pointer transition-all"
                                    value={formData.sourceType}
                                    onChange={e => {
                                        const selectedConn = connections.find(c => c.name === e.target.value);
                                        setFormData({
                                            ...formData,
                                            sourceType: e.target.value,
                                            description: selectedConn?.description || formData.description
                                        });
                                    }}
                                >
                                    <option value="" disabled>{loadingConnections ? 'Loading...' : 'Select connection...'}</option>
                                    {connections.map(conn => (
                                        <option key={conn.id} value={conn.name}>
                                            {conn.name} ({conn.connectorKey})
                                        </option>
                                    ))}
                                </select>
                                <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rotate-90 text-gray-500 pointer-events-none" />
                            </div>
                        </div>

                        {formData.sourceType && (
                            <div className="flex items-center gap-2 py-1 flex-wrap">
                                <button
                                    onClick={handleSchemaDiscovery}
                                    disabled={loadingSchema}
                                    className="inline-flex items-center gap-2 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 px-3 py-1.5 rounded-lg border border-indigo-500/30 text-xs font-medium transition-all hover:border-indigo-500/50"
                                >
                                    {loadingSchema ? (
                                        <span className="w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <Search className="w-3.5 h-3.5" />
                                    )}
                                    Schema Discovery
                                </button>
                                {isStructuredDatabase(getSelectedConnection()?.connectorKey) && (
                                    <button
                                        type="button"
                                        onClick={() => setShowQueryBuilder(true)}
                                        className="inline-flex items-center gap-2 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 px-3 py-1.5 rounded-lg border border-cyan-500/30 text-xs font-medium transition-all hover:border-cyan-500/50"
                                    >
                                        <Terminal className="w-3.5 h-3.5" />
                                        Query builder
                                    </button>
                                )}
                                <span className="text-[10px] text-gray-500">Select object to auto-fill details</span>
                            </div>
                        )}

                        <div className="space-y-1">
                            <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Connection Details</label>
                            <textarea
                                placeholder="Connection string, query, or paste from schema..."
                                className="w-full bg-neutral-800/80 border border-neutral-700/50 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 min-h-[100px] focus:ring-1 focus:ring-indigo-500/60 focus:border-indigo-500/40 outline-none resize-y custom-scrollbar transition-all"
                                value={formData.connectionDetails}
                                onChange={e => setFormData({ ...formData, connectionDetails: e.target.value })}
                            />
                        </div>

                        {formData.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {formData.tags.map(tag => (
                                    <span
                                        key={tag}
                                        className="inline-flex items-center gap-1 bg-indigo-500/15 text-indigo-300 text-[11px] px-2 py-0.5 rounded-md border border-indigo-500/25"
                                    >
                                        {tag}
                                        <button
                                            onClick={() => handleRemoveTag(tag)}
                                            className="hover:text-white transition-colors"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Right: Expected metadata structure */}
                    <div className="flex-1 min-w-[320px] flex flex-col border-l border-neutral-800/80 pl-4">
                        <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1.5 shrink-0 mb-1.5">
                            <FileJson className="w-3.5 h-3.5 text-amber-400/80" />
                            Expected metadata structure
                        </label>
                        <div className="flex-1 min-h-0 rounded-lg border border-neutral-700/50 bg-neutral-950 overflow-hidden flex flex-col">
                            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto custom-scrollbar">
                                {metadataPreview ? (
                                    <SyntaxHighlighter
                                        language="json"
                                        style={vscDarkPlus}
                                        customStyle={{ margin: 0, padding: '0.75rem 1rem', background: 'transparent', fontSize: '11px', lineHeight: 1.4, minHeight: '100%' }}
                                        showLineNumbers={false}
                                        codeTagProps={{ style: { fontFamily: 'ui-monospace, monospace' } }}
                                    >
                                        {metadataPreview}
                                    </SyntaxHighlighter>
                                ) : (
                                    <div className="p-4 flex items-center justify-center h-full min-h-[200px] text-gray-500 text-sm">
                                        Use Schema Discovery and select objects from the tree to build a metadata template. It will appear here.
                                    </div>
                                )}
                            </div>
                        </div>
                        {metadataPreview && (
                            <p className="text-[10px] text-gray-500 mt-1.5 shrink-0">This is the metadata model generated from your current schema selection.</p>
                        )}
                    </div>
                </div>

                {/* Footer - compact */}
                <div className="px-4 py-3 border-t border-neutral-800/80 bg-neutral-900/50 flex justify-end items-center gap-2">
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-neutral-800 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onSubmit(formData)}
                        className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold px-4 py-1.5 rounded-lg shadow-lg shadow-indigo-900/30 transition-all"
                    >
                        Create Source
                    </button>
                </div>

            </div>

            {showTypeSelector && (
                <SourceTypeSelectorModal
                    onClose={() => setShowTypeSelector(false)}
                    onSelect={(type) => {
                        setFormData({ ...formData, sourceType: type });
                        setShowTypeSelector(false);
                    }}
                    currentSelection={formData.sourceType}
                />
            )}
            {showSchemaDiscovery && (
                <SchemaDiscoveryModal
                    onClose={() => setShowSchemaDiscovery(false)}
                    schemaData={schemaData}
                    onExpand={handleExpandNode}
                    sourceType={getSelectedConnection()?.connectorKey}
                    onSelect={(paths) => {
                        // Keep a simple summary in connectionDetails if metadata generation fails
                        if (!paths.length) return;
                        setFormData(prev => ({
                            ...prev,
                            connectionDetails: prev.connectionDetails || paths.join(', ')
                        }));
                    }}
                    onBuildMetadata={(json) => {
                        setFormData(prev => ({
                            ...prev,
                            connectionDetails: json
                        }));
                        setMetadataPreview(json);
                        setShowSchemaDiscovery(false);
                    }}
                />
            )}
            {showQueryBuilder && getSelectedConnection() && (
                <QueryBuilderModal
                    onClose={() => setShowQueryBuilder(false)}
                    datastoreId={getSelectedConnection()!.id}
                    connectionName={getSelectedConnection()!.name || formData.sourceType}
                    onSubmitTemplate={(json) => {
                        setFormData(prev => ({
                            ...prev,
                            connectionDetails: json
                        }));
                        setMetadataPreview(json);
                        setShowQueryBuilder(false);
                    }}
                />
            )}
        </div>
    );
};

export default IngestionEntityModal;
